import { useAuth } from "@clerk/clerk-react";
import { Box, Switch, Typography } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { fetcherAuth } from "utils/api";

export default function AutopilotSettings() {
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const { setErrorAlert } = useContext(AlertContext);
  const navigate = useNavigate();

  const [pauseAll, setPauseAll] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchSettings = useCallback(async () => {
    if (!currentOrg?.id) return;
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/outreach-automation-settings`,
      );
      setPauseAll(res.outreachAutomationSetting?.pauseAll ?? false);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  }, [currentOrg?.id, getToken, setErrorAlert]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleToggle = async () => {
    if (!currentOrg?.id) return;
    const newValue = !pauseAll;
    setPauseAll(newValue);
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/outreach-automation-settings`,
        "PUT",
        {},
        { pauseAll: newValue },
      );
    } catch (error) {
      setPauseAll(!newValue);
      setErrorAlert(error);
    }
  };

  return (
    <Box sx={{ py: 4, px: { xs: 2, sm: 3 }, maxWidth: 600 }}>
      <Box
        component="span"
        className="fa-solid fa-arrow-left"
        onClick={() => navigate("/dashboard/autopilot")}
        sx={{
          fontSize: 14,
          color: "primary.main",
          cursor: "pointer",
          mb: 2,
          display: "inline-block",
        }}
      />
      <Typography
        sx={{ fontWeight: 700, fontSize: 22, color: "primary.main", mt: 1 }}
      >
        Automation Settings
      </Typography>
      <Typography sx={{ fontSize: 14, color: "rgba(1,67,57,0.6)", mt: 0.5 }}>
        Manage global settings for your brand automations.
      </Typography>

      {!loading && (
        <Box
          sx={{
            mt: 3,
            p: 2.5,
            borderRadius: "12px",
            border: "1px solid rgba(1,67,57,0.1)",
            backgroundColor: "#fafaf6",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Typography
              sx={{ fontWeight: 600, fontSize: 15, color: "primary.main" }}
            >
              Pause All Automations
            </Typography>
            <Typography
              sx={{ fontSize: 13, color: "rgba(1,67,57,0.55)", mt: 0.5 }}
            >
              When enabled, all automations will stop discovering and pitching
              new brands. Any emails already scheduled will still be delivered.
            </Typography>
          </Box>
          <Switch
            checked={pauseAll}
            onChange={handleToggle}
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": {
                color: "#014339",
              },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                backgroundColor: "#014339",
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
}
